/**
 * Floating Action Button (FAB)
 * Fixed bottom-right toggle for the AI chat.
 * Uses RobotIcon; pulses when closed to draw attention, pauses when open.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAIContext } from './AIContext';
import RobotIcon from '../icons/RobotIcon';

const POSITION_STORAGE_KEY = 'balancio_ai_position';

const FloatingButton: React.FC = () => {
  const { toggleOpen, isOpen, messages } = useAIContext();

  const hasNewMessages = messages.length > 0 && !isOpen;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const [isNotDefaultPosition, setIsNotDefaultPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(POSITION_STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.x !== 0 || parsed.y !== 0;
      }
    } catch {}
    return false;
  });

  useEffect(() => {
    const handlePositionChanged = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail) {
        setIsNotDefaultPosition(detail.x !== 0 || detail.y !== 0);
      }
    };
    window.addEventListener('balancio-position-changed', handlePositionChanged);
    return () => window.removeEventListener('balancio-position-changed', handlePositionChanged);
  }, []);

  const handleRestorePosition = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(POSITION_STORAGE_KEY, JSON.stringify({ x: 0, y: 0 }));
    setIsNotDefaultPosition(false);
    window.dispatchEvent(new CustomEvent('balancio-reset-position'));
  }, []);

  const buttonSize = isMobile ? 'w-12 h-12' : 'w-14 h-14';
  const bottomOffset = isMobile ? 'bottom-20' : 'bottom-6';
  const rightOffset = isMobile ? 'right-4' : 'right-6';
  const shouldPulse = !isOpen;

  return (
    <>
      <div
        data-tour="fab-ai"
        className={`fixed ${bottomOffset} ${rightOffset} z-40 ${buttonSize}`}
      >
        {shouldPulse && (
          <span
            className="ai-fab-ring pointer-events-none absolute inset-0 rounded-full"
            style={{
              border: '2px solid var(--primary)',
              backgroundColor: 'transparent',
            }}
            aria-hidden="true"
          />
        )}

        <button
          onClick={toggleOpen}
          title={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
          className={`relative z-10 flex items-center justify-center w-full h-full rounded-full shadow-lg transition-shadow duration-300 hover:shadow-xl active:scale-95 ${
            shouldPulse ? 'ai-fab-pulse' : ''
          }`}
          style={{
            backgroundColor: isOpen ? 'var(--danger)' : 'var(--light)',
            color: isOpen ? '#FFFFFF' : 'var(--primary)',
            border: isOpen ? 'none' : '2px solid var(--primary)',
            boxShadow: shouldPulse
              ? '0 4px 14px rgba(0, 205, 21, 0.35)'
              : undefined,
          }}
          aria-label={isOpen ? 'Close AI Assistant' : 'Open AI Assistant'}
        >
          {isOpen ? (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-6 w-6 sm:h-7 sm:w-7"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <RobotIcon size={isMobile ? 26 : 30} />
          )}

          {hasNewMessages && (
            <span
              className="absolute -top-1 -right-1 flex items-center justify-center w-5 h-5 rounded-full text-xs font-bold"
              style={{
                backgroundColor: 'var(--danger)',
                color: '#FFFFFF',
              }}
            >
              {messages.length}
            </span>
          )}
        </button>
      </div>

      {isOpen && isNotDefaultPosition && !isMobile && (
        <button
          onClick={handleRestorePosition}
          title="Reset chat to default position"
          className="fixed bottom-24 right-6 z-40 flex items-center justify-center w-10 h-10 rounded-full shadow-lg transition-all duration-300 hover:scale-110 active:scale-95"
          style={{
            backgroundColor: 'var(--bg-grey-light)',
            color: 'var(--dark)',
            border: '1px solid var(--grey-light)',
          }}
          aria-label="Reset chat position"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
            />
          </svg>
        </button>
      )}
    </>
  );
};

export default FloatingButton;
